import React, { useEffect, useRef, useState } from 'react';
import { Box, Grid, Typography } from '@mui/material';
import { useParams } from 'react-router-dom';
import { useTranslations } from 'hooks/useTranslations';
import { useGlobalProperty } from 'models/globalPropertyContext';
import { useThemeBreakpoint } from 'hooks/useThemeBreakpoint';
import Breadcrumbs from 'components/atoms/Breadcrumbs';
import { StyledPageTitle } from 'theme/styles';
import { ERouteLinks } from 'models/route';
import { Loader } from 'components/atoms/Loader';
import { Chips } from 'components/atoms/Chips';
import { Table } from 'components/molecules/Table';
import { CardList, emptyFieldHolder } from 'components/molecules/CardList';
import { Pagination } from 'components/molecules/Pagination';
import { EPerPages, ISortOptions } from 'models/table';
import { formatDate } from 'utils/date';
import { findInDictionary } from 'helpers/dictionary';
import {
  StyledValueDetails,
  StyledValueDetailsHeader,
} from 'pages/LicenceAuthoritiesPage/styles';
import { useLicences } from '../hooks';
import {
  batchHistoryTableColumns,
  batchPrintoutsTableColumns,
  batchStatusVariant,
  mapBatchHistoryToDataSource,
  mapPrintoutsToDataSource,
} from '../helpers';
import { BatchPrintoutTableCard } from 'pages/LicencesPage/PrintoutBatchDetails/BatchPrintoutTableCard/BatchPrintoutTableCard';
import { BatchHistoryTableCard } from 'pages/LicencesPage/PrintoutBatchDetails/BatchHistoryTableCard/BatchHistoryTableCard';
import { SftpAlertBanner } from '../SftpAlertBanner/SftpAlertBanner';
import { useLang } from 'models/langContext';
import { useDictionary } from 'models/dictionaryContext';
import { ELicencePrintBatchStatus } from 'constants/LicencePrintBatchStatus';

export const PrintoutBatchDetails = () => {
  const { t } = useTranslations();
  const { id = '' } = useParams<{ id: string }>();
  const { isDesktop } = useThemeBreakpoint();
  const { longDateFormat, getProperties } = useGlobalProperty();
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<EPerPages>(EPerPages.perPage25);
  const [sort, setSort] = useState<ISortOptions | null>(null);
  const [isSortLoaded, setIsSortLoaded] = useState<boolean>(true);
  const [isLoadingPrintouts, setIsLoadingPrintouts] = useState<boolean>(true);
  const [historyCurrentPage, setHistoryCurrentPage] = useState<number>(1);
  const [historyPerPage, setHistoryPerPage] = useState<EPerPages>(EPerPages.perPage25);
  const [isLoadingHistory, setIsLoadingHistory] = useState<boolean>(true);
  const { selectedLanguage } = useLang();
  const scrollPosition = useRef(0);
  const {
    batchDetails,
    getBatchDetailsData,
    batchPrintouts,
    batchPrintoutsPaginator,
    getBatchPrintoutsData,
    batchHistory,
    batchHistoryPaginator,
    getBatchHistoryData,
    weaponLicencingAuthority,
    getWeaponLicencingAuthority,
    weaponLicencePrintBatchStatus,
    getWeaponLicencePrintBatchStatus,
    weaponLicencePrintStatus,
    getWeaponLicencePrintStatus,
  } = useLicences();
  const { weaponLicencePrintType, getWeaponLicencePrintType } = useDictionary();

  useEffect(() => {
    getProperties();
  }, []);

  useEffect(() => {
    getWeaponLicencePrintStatus();
    getWeaponLicencingAuthority();
    getWeaponLicencePrintBatchStatus();
    getWeaponLicencePrintType();
  }, [selectedLanguage]);

  useEffect(() => {
    (async function init() {
      setIsLoading(true);
      await getBatchDetailsData(id);
      setIsLoading(false);
    })();
  }, [id]);

  useEffect(() => {
    (async () => {
      setIsLoadingHistory(true);
      await getBatchHistoryData(id, historyCurrentPage, historyPerPage);
      setIsLoadingHistory(false);
    })();
  }, [id, historyCurrentPage, historyPerPage]);

  useEffect(() => {
    (async () => {
      setIsLoadingPrintouts(true);
      await getBatchPrintoutsData(id, currentPage, perPage, sort);
      setIsLoadingPrintouts(false);
      setIsSortLoaded(true);
    })();
  }, [id, currentPage, perPage, sort]);

  useEffect(() => {
    if (isSortLoaded) {
      window.scrollTo({ top: scrollPosition.current, behavior: 'auto' });
      scrollPosition.current = 0;
    }
  }, [isSortLoaded]);

  return (
    <>
      <Breadcrumbs
        items={[
          { label: t('module.licences.name'), route: ERouteLinks.LicencesPage },
          {
            label: t('licences.batches.header'),
            route: `${ERouteLinks.LicencesPage}?expand=batches`,
          },
          t('licences.batchDetails.header'),
        ]}
      />
      <StyledPageTitle variant="h4" sx={{ mb: 2 }}>
        {t('licences.batchDetails.header')}
      </StyledPageTitle>

      {batchDetails &&
        ![
          ELicencePrintBatchStatus.REJECTED,
          ELicencePrintBatchStatus.PROCESSED,
          ELicencePrintBatchStatus.FAILED_TO_SEND,
        ].includes(batchDetails.statusUniqueId as ELicencePrintBatchStatus) && (
          <SftpAlertBanner
            licenceAuthorityUniqueId={batchDetails?.licencingAuthorityUniqueId}
          />
        )}

      {isLoading ? (
        <Loader isLoading={true} />
      ) : (
        <Grid container sx={{ gap: 3 }}>
          <StyledValueDetails container size={{ xs: 12 }} columnSpacing="10px">
            <Grid size={{ xs: 12, md: 4 }}>
              <Typography variant="subtitle2" sx={{ marginBottom: 0.5 }}>
                {t('licences.batchName.label')}
              </Typography>
              <Typography variant="body2">{batchDetails?.name ?? '-'}</Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Typography variant="subtitle2" sx={{ marginBottom: 0.5 }}>
                {t('licences.licenceAuthority.label')}
              </Typography>
              <Typography variant="body2">
                {emptyFieldHolder(
                  findInDictionary(
                    batchDetails?.licencingAuthorityUniqueId ?? '',
                    weaponLicencingAuthority,
                  ),
                )}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Typography variant="subtitle2" sx={{ marginBottom: 0.5 }}>
                {t('licences.batchStatus.label')}
              </Typography>
              <Chips
                text={emptyFieldHolder(
                  findInDictionary(
                    batchDetails?.statusUniqueId ?? '',
                    weaponLicencePrintBatchStatus,
                  ),
                )}
                variant={batchStatusVariant(batchDetails?.statusUniqueId)}
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Typography variant="subtitle2" sx={{ marginBottom: 0.5 }}>
                {t('licences.createDate.label')}
              </Typography>
              <Typography variant="body2">
                {emptyFieldHolder(formatDate(batchDetails?.createdDate, longDateFormat))}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Typography variant="subtitle2" sx={{ marginBottom: 0.5 }}>
                {t('licences.manifestDate.label')}
              </Typography>
              <Typography variant="body2">
                {emptyFieldHolder(formatDate(batchDetails?.manifestDate, longDateFormat))}
              </Typography>
            </Grid>
          </StyledValueDetails>

          <Grid size={{ xs: 12 }}>
            <StyledValueDetailsHeader>
              {t('licences.printoutsList.label')}
            </StyledValueDetailsHeader>
            <Box sx={{ mt: 3 }}>
              {isDesktop ? (
                <Table
                  columns={batchPrintoutsTableColumns({ t })}
                  loading={isLoadingPrintouts}
                  dataSource={mapPrintoutsToDataSource(
                    batchPrintouts,
                    weaponLicencePrintStatus,
                    longDateFormat,
                    weaponLicencePrintType,
                  )}
                  onSort={(it) => {
                    setSort(it);
                    setIsSortLoaded(false);
                    scrollPosition.current = window.scrollY;
                  }}
                  isSortLoaded={isSortLoaded}
                />
              ) : (
                <CardList
                  items={mapPrintoutsToDataSource(
                    batchPrintouts,
                    weaponLicencePrintStatus,
                    longDateFormat,
                    weaponLicencePrintType,
                  )}
                  loading={isLoadingPrintouts}
                  render={(data, index) => <BatchPrintoutTableCard data={data} key={index} />}
                />
              )}
              {batchPrintoutsPaginator && (
                <Pagination
                  count={batchPrintoutsPaginator.totalElements}
                  onChangePage={(page) => setCurrentPage(page)}
                  current={currentPage}
                  perPage={perPage}
                  onChangePerPage={(value) => setPerPage(value)}
                  isLoading={false}
                />
              )}
            </Box>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <StyledValueDetailsHeader>
              {t('licences.batchHistory.label')}
            </StyledValueDetailsHeader>
            <Box sx={{ mt: 3 }}>
              {isDesktop ? (
                <Table
                  columns={batchHistoryTableColumns({ t })}
                  loading={isLoadingHistory}
                  dataSource={mapBatchHistoryToDataSource(
                    batchHistory,
                    weaponLicencePrintBatchStatus,
                    longDateFormat,
                  )}
                />
              ) : (
                <CardList
                  items={mapBatchHistoryToDataSource(
                    batchHistory,
                    weaponLicencePrintBatchStatus,
                    longDateFormat,
                  )}
                  loading={isLoadingHistory}
                  render={(data, index) => <BatchHistoryTableCard data={data} key={index} />}
                />
              )}
              {batchHistoryPaginator && (
                <Pagination
                  count={batchHistoryPaginator.totalElements}
                  onChangePage={(page) => setHistoryCurrentPage(page)}
                  current={historyCurrentPage}
                  perPage={historyPerPage}
                  onChangePerPage={(value) => setHistoryPerPage(value)}
                  isLoading={false}
                />
              )}
            </Box>
          </Grid>
        </Grid>
      )}
    </>
  );
};
